<!--
� 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
-->

<section class="hardware-assets-realtime">
	@let canSeeTotalCo2 = authorizationService.canSee(grants.totalCo2Emissions);
	@let canSeeOperational = authorizationService.canSee(grants.operationalEmissions);
	@let canSeeEmbodied = authorizationService.canSee(grants.embodiedEmissions);
	@let canSeeEmissionsOverTime = authorizationService.canSee(grants.totalEmissionsOverTime);
	@let canSeeModels = authorizationService.canSee(grants.deviceAnalysisModels);
	@let canSeeManufacturer =
		authorizationService.canSee(grants.deviceAnalysisTotalEmissionByManufacturer);
	@let canSeeGeography = authorizationService.canSee(grants.locationsGeographicAnalysis);
	@let canSeePerDeviceLocation =
		authorizationService.canSee(grants.locationsPerDeviceEmissionsByLocation);
	@let canSeeTotalLocation =
		authorizationService.canSee(grants.locationsTotalEmissionsByLocation);
	@let canSeeLocationTrend =
		authorizationService.canSee(grants.locationsTotalOverTimePerLocation);
	@let canSeeLifecycleSavings =
		authorizationService.canSee(grants.embodiedEmissionsSavedLifecycleEmissions);
	@let canSeeDeviceSection = canSeeModels || canSeeManufacturer;
	@let canSeeLocationsSection =
		canSeeGeography || canSeePerDeviceLocation || canSeeTotalLocation || canSeeLocationTrend;
	@let activeAuthorizedSection =
		getActiveSection(canSeeDeviceSection, canSeeLocationsSection, canSeeLifecycleSavings);

	<div class="top-bar">
		<div class="view-switch">
			<globalictcc-switch
				[options]="viewSwitchOptions"
				(handleSwitch)="onViewSwitch($event)"
			/>
		</div>

		@if (
			canSeeTotalCo2 ||
			canSeeOperational ||
			canSeeEmbodied ||
			canSeeEmissionsOverTime ||
			canSeeModels
		) {
			<div class="filters">
				<div class="period">
					<globalictcc-datepicker
						label="Time period"
						[formControl]="periodControl"
					/>
					<globalictcc-button
						label="Apply"
						[disabled]="!periodControl.value"
						(handleClick)="onApply()"
					/>
				</div>
			</div>
		}
	</div>

	@if (canSeeTotalCo2 || canSeeOperational || canSeeEmbodied) {
		@let information = information$ | async;
		@let info = information?.data;
		<div class="overview">
			<div class="kpis">
				@if (canSeeTotalCo2) {
					<globalictcc-kpi
						label="Total CO2e Emissions"
						[value]="info?.totalEmissions ?? 0"
						[unit]="'tCO2e'"
					/>
				}
				@if (canSeeOperational) {
					<globalictcc-kpi
						label="Operational emissions"
						[value]="info?.operationalEmissions ?? 0"
						[unit]="'tCO2e'"
					/>
				}
				@if (canSeeEmbodied) {
					<globalictcc-kpi
						label="Embodied emissions"
						[value]="info?.embodiedEmissions ?? 0"
						[unit]="'tCO2e'"
					/>
				}
			</div>

			<section class="summary">
				<div class="summary-title">
					<span class="title">This is equivalent to</span>
				</div>
				<div class="summary-cards">
					<div class="summary-card">
						<span class="icon-home"></span>
						<span class="card-title">
							{{ info?.houses || 0 | maxDecimals: 2 }} Houses
						</span>
						<span class="card-description">
							Equivalent to CO2 emissions generated by
							{{ info?.houses || 0 | maxDecimals: 2 }} houses over the same period
						</span>
					</div>
					<div class="summary-card">
						<span class="icon-car"></span>
						<span class="card-title">
							{{ info?.kmCarDrive || 0 | maxDecimals: 2 }} Cars
						</span>
						<span class="card-description">
							Equivalent to CO2 emissions generated by driving
							{{ info?.kmCarDrive || 0 | maxDecimals: 2 }} cars from London to Paris
						</span>
					</div>
				</div>
			</section>
		</div>
	}

	@if (canSeeEmissionsOverTime) {
		@let totalEmissions = totalEmissions$ | async;
		<section class="chart">
			<div class="section-title">
				<span class="chart-title">Total emissions over time</span>
				<div class="section-actions">
					<globalictcc-button
						label="Export chart"
						iconName="download"
						[textOnly]="true"
						(handleClick)="
							onExportChart(
								totalEmissionsChartRef.instance,
								'hardware_realtime_total_emissions_over_time'
							)
						"
					/>
					<globalictcc-button
						label="Download as CSV"
						iconName="download"
						[textOnly]="true"
					/>
					<globalictcc-button
						label="Export report"
						iconName="document"
						[textOnly]="true"
					/>
					<globalictcc-button
						label="Share report"
						iconName="share"
						[textOnly]="true"
					/>
				</div>
			</div>
			<globalictcc-chart
				#totalEmissionsChartRef
				chartType="line"
				[isLegendVisible]="true"
				[isTitleVisible]="false"
				[data]="totalEmissions?.data?.chart"
				[hasError]="totalEmissions?.hasError || totalEmissions?.data?.hasError"
				[isLoading]="totalEmissions?.isLoading ?? true"
			/>
		</section>
	}

	@if (canSeeDeviceSection || canSeeLocationsSection || canSeeLifecycleSavings) {
		<div class="section-tabs">
			<globalictcc-chips
				[chips]="
					getVisibleSectionChips(
						canSeeDeviceSection,
						canSeeLocationsSection,
						canSeeLifecycleSavings
					)
				"
				(handleSelection)="onSectionChange($event)"
			/>
		</div>
	}
	<div class="dashboard-data-region">
		@if (activeAuthorizedSection === "device" && canSeeModels) {
			@let modelImpact = modelImpact$ | async;
			<section
				class="chart models-impact"
				[class.chart--after-tabs]="true"
			>
				<div class="section-title">
					<span class="chart-title">Highest environmental impact - Models</span>
					<div class="section-actions">
						<globalictcc-button
							label="Export total"
							[textOnly]="true"
							iconName="download"
							(handleClick)="
								onExportChart(
									modelsTotalChartRef.instance,
									'hardware_realtime_models_total'
								)
							"
						/>
						<globalictcc-button
							label="Export per device"
							[textOnly]="true"
							iconName="download"
							(handleClick)="
								onExportChart(
									modelsPerDeviceChartRef.instance,
									'hardware_realtime_models_per_device'
								)
							"
						/>
					</div>
				</div>
				<div class="chart-grid">
					<div class="chart-wrapper">
						<span class="chart-label">Total</span>
						<globalictcc-chart
							#modelsTotalChartRef
							chartType="barY"
							[data]="modelImpact?.data?.[0]?.chart"
							[hasError]="modelImpact?.hasError || modelImpact?.data?.[0]?.hasError"
							[isLoading]="modelImpact?.isLoading ?? true"
							[isTitleVisible]="false"
						/>
					</div>
					<div class="chart-wrapper">
						<span class="chart-label">Per device</span>
						<globalictcc-chart
							#modelsPerDeviceChartRef
							chartType="barY"
							[data]="modelImpact?.data?.[1]?.chart"
							[hasError]="modelImpact?.hasError || modelImpact?.data?.[1]?.hasError"
							[isLoading]="modelImpact?.isLoading ?? true"
							[isTitleVisible]="false"
						/>
					</div>
				</div>
			</section>
		}

		@if (activeAuthorizedSection === "device" && canSeeManufacturer) {
			@let manufacturerData = manufacturerData$ | async;
			<section
				class="chart manufacturer-emissions"
				[class.chart--after-tabs]="!canSeeModels"
			>
				<div class="section-title">
					<span class="chart-title"
						>Total emission by manufacturer <span class="icon-warning"></span
					></span>
					<globalictcc-button
						label="Export chart"
						[textOnly]="true"
						iconName="download"
						(handleClick)="
							onExportChart(
								manufacturerPieChartRef.instance,
								'hardware_realtime_manufacturer_emissions'
							)
						"
					/>
				</div>
				<div class="filter">
					<globalictcc-dropdown
						label="Category"
						[options]="categoriesOptions"
						[formControl]="categoriesControl"
					/>
					<globalictcc-button
						label="Apply"
						(handleClick)="onApply()"
					/>
				</div>
				<div class="table-chart">
					<div class="table">
						<globalictcc-table
							[gridOptions]="manufacturersGridOptions"
							[hasError]="manufacturerData?.hasError || manufacturersTableHasError"
						/>
					</div>
					<globalictcc-chart
						#manufacturerPieChartRef
						chartType="pie"
						height="471px"
						labelPosition="outside"
						[isTitleVisible]="false"
						[data]="manufacturerData?.data?.[1]?.chart"
						[hasError]="
							manufacturerData?.hasError || manufacturerData?.data?.[1]?.hasError
						"
						[isLoading]="manufacturerData?.isLoading ?? true"
					/>
				</div>
			</section>
		}

		@if (activeAuthorizedSection === "locations") {
			@if (canSeePerDeviceLocation || canSeeTotalLocation) {
				<div class="filter">
					<globalictcc-dropdown
						label="Country"
						[options]="countriesOptions"
						[formControl]="countriesControl"
						(handleSelectionChange)="onCountryChange($event)"
					/>
				</div>
			}
			@if (canSeeGeography) {
				@let geography = geographicAnalysis$ | async;
				<section
					class="chart geographic-analysis"
					[class.chart--after-tabs]="true"
				>
					<div class="section-title">
						<span class="chart-title">Geographic analysis</span>
						<span class="icon-warning"></span>
					</div>
					<div class="map-wrapper">
						<globalictcc-map
							class="map-chart"
							[isTitleVisible]="false"
							[data]="geography?.data?.chart"
							[hasError]="geography?.hasError || geography?.data?.hasError"
						/>
					</div>
				</section>
			}
			@if (canSeePerDeviceLocation) {
				@let locationEmissions = locationEmissions$ | async;
				<section
					class="chart"
					[class.chart--after-tabs]="!canSeeGeography"
				>
					<div class="section-title">
						<span class="chart-title">Per device emissions by location</span>
						<globalictcc-button
							label="Export chart"
							[textOnly]="true"
							iconName="download"
							(handleClick)="
								onExportChart(
									perDeviceEmissionsChartRef.instance,
									'hardware_realtime_per_device_by_location'
								)
							"
						/>
					</div>
					<globalictcc-chart
						#perDeviceEmissionsChartRef
						chartType="barY"
						[isTitleVisible]="false"
						[data]="locationEmissions?.data?.chart"
						[hasError]="
							locationEmissions?.hasError || locationEmissions?.data?.hasError
						"
						[isLoading]="locationEmissions?.isLoading ?? true"
					/>
				</section>
			}
			@if (canSeeTotalLocation) {
				@let locationEmissions = locationEmissions$ | async;
				<section
					class="chart"
					[class.chart--after-tabs]="!canSeeGeography && !canSeePerDeviceLocation"
				>
					<div class="section-title">
						<span class="chart-title">Total emissions by location</span>
						<globalictcc-button
							label="Export chart"
							[textOnly]="true"
							iconName="download"
							(handleClick)="
								onExportChart(
									locationsChartRef.instance,
									'hardware_realtime_total_by_location'
								)
							"
						/>
					</div>
					<globalictcc-chart
						#locationsChartRef
						chartType="barY"
						[isTitleVisible]="false"
						[data]="locationEmissions?.data?.chart"
						[hasError]="
							locationEmissions?.hasError || locationEmissions?.data?.hasError
						"
						[isLoading]="locationEmissions?.isLoading ?? true"
					/>
				</section>
			}
			@if (canSeeLocationTrend) {
				@let locationTrend = locationTimeSeries$ | async;
				<section
					class="chart"
					[class.chart--after-tabs]="
						!canSeeGeography && !canSeePerDeviceLocation && !canSeeTotalLocation
					"
				>
					<div class="section-title">
						<span class="chart-title">Total over time per location</span>
						<globalictcc-button
							label="Export chart"
							[textOnly]="true"
							iconName="download"
							(handleClick)="
								onExportChart(
									energyConsumptionChartRef.instance,
									'hardware_realtime_total_over_time_by_location'
								)
							"
						/>
					</div>
					<globalictcc-chart
						#energyConsumptionChartRef
						chartType="line"
						[isLegendVisible]="true"
						[isTitleVisible]="false"
						[data]="locationTrend?.data?.chart"
						[hasError]="locationTrend?.hasError || locationTrend?.data?.hasError"
						[isLoading]="locationTrend?.isLoading ?? true"
					/>
				</section>
			}
		}

		@if (activeAuthorizedSection === "embodied" && canSeeLifecycleSavings) {
			@let lifecycle = savedLifecycleEmissions$ | async;
			<section class="chart chart--after-tabs">
				<div class="section-title">
					<span class="chart-title">Saved lifecycle emissions</span>
					<globalictcc-button
						label="Export chart"
						[textOnly]="true"
						iconName="download"
						(handleClick)="
							onExportChart(
								embodiedEmissionsChartRef.instance,
								'hardware_realtime_saved_lifecycle_emissions'
							)
						"
					/>
				</div>
				<globalictcc-chart
					#embodiedEmissionsChartRef
					chartType="line"
					[isLegendVisible]="true"
					[isTitleVisible]="false"
					[data]="lifecycle?.data?.chart"
					[hasError]="lifecycle?.hasError || lifecycle?.data?.hasError"
					[isLoading]="lifecycle?.isLoading ?? true"
				/>
			</section>
		}

		@if (isActiveSectionLoading(activeAuthorizedSection)) {
			<div
				class="loading"
				role="status"
				aria-live="polite"
			>
				<globalictcc-spinner />
				<span class="message">Loading...</span>
			</div>
		}
	</div>

	<div class="bottom-actions">
		<globalictcc-button
			label="Export report"
			iconName="document"
			[textOnly]="true"
			(handleClick)="onExportReport()"
		/>
	</div>
</section>
